import React, { useEffect, useState, useRef } from "react";
import { useUser } from "../contexts/UserContext";
import axiosClient from "../axios";
import { useLocation, useNavigate } from "react-router-dom";

/* =========================
   LOAD RAZORPAY SDK
========================== */
const loadRazorpay = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const CheckoutPage = () => {
  const [activeTab, setActiveTab] = useState("activation");
  const location = useLocation();
  const { user } = useUser();
  const navigate = useNavigate();

  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [priceBase, setPriceBase] = useState(0);
  const [tax, setTax] = useState(0);
  const [loadingPrice, setLoadingPrice] = useState(true);
  const [coupon, setCoupon] = useState("");
  const [discountedSubtotal, setDiscountedSubtotal] = useState(0);
  const [applied, setApplied] = useState(false);
  const [loadingCoupon, setLoadingCoupon] = useState(false);
  const [paying, setPaying] = useState(false);
  const [activationCode, setActivationCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const autoAppliedRef = useRef(false);

  /* =========================
     FETCH COURSES
  ========================== */
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await axiosClient.get("/api/fetch-categories");
        const parentCourses = response.data.data.terms.filter(
          (term) => term.parent_id === null,
        );
        setCourses(parentCourses);
      } catch (error) {
        console.error("Failed to fetch classes", error);
      }
    };
    fetchCourses();
  }, []);

  /* =========================
     FETCH PRICING
  ========================== */
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await axiosClient.get("/api/option/pricing");
        if (res.data?.option_value) {
          const data = JSON.parse(res.data.option_value);
          setPriceBase(parseFloat(data.price.replace(/[^\d.]/g, "")));
          setTax(parseFloat(data.tax || 0));
        }
      } catch (err) {
        console.error(err);
        alert("Failed to load pricing");
      } finally {
        setLoadingPrice(false);
      }
    };
    fetchPricing();
  }, []);

  /* =========================
     AUTO APPLY REFERRAL
  ========================== */
  useEffect(() => {
    if (autoAppliedRef.current || !priceBase || loadingPrice) return;
    const referralCode = user?.referral_code || location.state?.referral_code;
    if (!referralCode) return;
    autoAppliedRef.current = true;
    const applyReferral = async () => {
      try {
        setLoadingCoupon(true);
        const { data } = await axiosClient.post("/api/coupons/apply", {
          code: referralCode,
          subtotal: priceBase,
        });
        if (data.discounted_amount !== undefined) {
          setCoupon(referralCode);
          setDiscountedSubtotal(data.discounted_amount);
          setApplied(true);
        }
      } catch (err) {
        console.error(err);
        autoAppliedRef.current = false;
      } finally {
        setLoadingCoupon(false);
      }
    };
    applyReferral();
  }, [
    user?.referral_code,
    location.state?.referral_code,
    priceBase,
    loadingPrice,
  ]);

  /* =========================
     APPLY COUPON
  ========================== */
  const handleApplyCoupon = async () => {
    if (!coupon) return;
    try {
      setLoadingCoupon(true);
      const { data } = await axiosClient.post("/api/coupons/apply", {
        code: coupon,
        subtotal: priceBase,
      });
      if (data.discounted_amount !== undefined) {
        setDiscountedSubtotal(data.discounted_amount);
        setApplied(true);
        autoAppliedRef.current = true;
      } else {
        resetCoupon();
        alert("Invalid coupon");
      }
    } catch (err) {
      console.error(err);
      resetCoupon();
      alert("Failed to apply coupon");
    } finally {
      setLoadingCoupon(false);
    }
  };

  const resetCoupon = () => {
    setDiscountedSubtotal(0);
    setApplied(false);
    setCoupon("");
    autoAppliedRef.current = false;
  };

  /* =========================
     CALCULATIONS
  ========================== */
  const effectiveSubtotal = applied ? discountedSubtotal : priceBase;
  const taxAmount = effectiveSubtotal * (tax / 100);
  const finalAmount = Math.round(effectiveSubtotal + taxAmount);

  /* =========================
     PAYMENT
  ========================== */
  const handleCheckout = async () => {
    if (!selectedCourse) {
      alert("Please select a class");
      return;
    }
    setPaying(true);
    try {
      const loaded = await loadRazorpay();
      if (!loaded) {
        alert("Razorpay SDK failed");
        setPaying(false);
        return;
      }
      const { data } = await axiosClient.post("/api/orders/create-order", {
        coupon: applied ? coupon : null,
        term_id: selectedCourse,
      });
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: data.amount,
        currency: "INR",
        order_id: data.razorpay_order_id,
        name: "NEXUSFLOW SERVICES LLP",
        description: "Course Purchase",
        handler: async (response) => {
          try {
            await axiosClient.post("/api/orders/verify-payment", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              order_id: data.id,
            });
            alert("Course purchased successfully 🎉");
            navigate("/learn");
          } catch (error) {
            console.error(error);
            alert("Payment verification failed");
          } finally {
            setPaying(false);
          }
        },
        modal: { ondismiss: () => setPaying(false) },
        theme: { color: "#0d6efd" },
      };
      new window.Razorpay(options).open();
    } catch (err) {
      console.error(err);

      alert(err.response?.data?.message || "Payment failed");

      setPaying(false);
    }
  };

  /* =========================
     REDEEM CODE
  ========================== */
  const redeemActivationCode = async () => {
    if (!selectedCourse) {
      alert("Please select a class");
      return;
    }
    if (!activationCode.trim()) {
      alert("Please enter activation code");
      return;
    }
    try {
      setRedeeming(true);
      const { data } = await axiosClient.post("/api/codes/redeem", {
        code: activationCode,
        term_id: selectedCourse,
      });
      alert(data.message || "Course unlocked successfully");
      setActivationCode("");
      setSelectedCourse("");
      navigate("/learn");
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "Failed to redeem code");
    } finally {
      setRedeeming(false);
    }
  };

  /* =========================
     LOADING STATE
  ========================== */
  if (loadingPrice) {
    return (
      <div className="min-vh-100 d-flex flex-column align-items-center justify-content-center bg-light gap-3">
        <div className="spinner-border text-primary" role="status" />
        <p className="text-secondary small mb-0">Preparing checkout…</p>
      </div>
    );
  }

  return (
    <div className="min-vh-100 bg-light py-5 px-3 d-flex align-items-center justify-content-center">
      <div className="w-100" style={{ maxWidth: 460 }}>
        {/* Header */}
        <div className="mb-4 ps-1">
          <p
            className="text-uppercase text-secondary mb-0"
            style={{ fontSize: "0.68rem", letterSpacing: "0.1em" }}
          >
            Secure Checkout
          </p>
        </div>

        {/* Card */}
        <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
          {/* Tabs */}
          <div className="d-flex border-bottom">
            <button
              className={`flex-fill py-3 border-0 bg-transparent fw-medium small text-uppercase d-flex align-items-center justify-content-center gap-2 ${
                activeTab === "activation"
                  ? "text-dark border-bottom border-dark border-2"
                  : "text-secondary"
              }`}
              style={{
                letterSpacing: "0.07em",
                borderBottom:
                  activeTab === "activation"
                    ? "2px solid #212529"
                    : "2px solid transparent",
              }}
              onClick={() => setActiveTab("activation")}
            >
              <i className="bi bi-key" />
              Activation
            </button>
            <button
              className={`flex-fill py-3 border-0 bg-transparent fw-medium small text-uppercase d-flex align-items-center justify-content-center gap-2 ${
                activeTab === "payment"
                  ? "text-dark border-bottom border-dark border-2"
                  : "text-secondary"
              }`}
              style={{
                letterSpacing: "0.07em",
                borderBottom:
                  activeTab === "payment"
                    ? "2px solid #212529"
                    : "2px solid transparent",
              }}
              onClick={() => setActiveTab("payment")}
            >
              <i className="bi bi-credit-card" />
              Payment
            </button>
          </div>

          <div className="p-4">
            {/* ── ACTIVATION TAB ── */}
            {activeTab === "activation" && (
              <>
                <h2 className="fs-4 fw-semibold text-dark mb-1">Redeem code</h2>
                <p className="text-secondary small mb-4">
                  Enter your activation key to unlock a course
                </p>
                <div className="alert alert-light border-start border-4 border-secondary rounded-3 py-3 px-3 mb-4 text-secondary small">
                  Have a code from your institution or a gift purchase? Select
                  your class and enter the code below for instant access.
                </div>
                {/* Class select */}
                <div className="mb-3">
                  <label
                    className="form-label text-uppercase text-secondary fw-medium"
                    style={{ fontSize: "0.68rem", letterSpacing: "0.09em" }}
                  >
                    Select class
                  </label>
                  <select
                    className="form-select form-select-sm rounded-3 bg-light border-1 py-2 px-3"
                    value={selectedCourse}
                    onChange={(e) => setSelectedCourse(e.target.value)}
                  >
                    <option value="">Choose a class…</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.name}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Activation code */}

                <div className="mb-4">
                  <label
                    className="form-label text-uppercase text-secondary fw-medium"
                    style={{ fontSize: "0.68rem", letterSpacing: "0.09em" }}
                  >
                    Activation code
                  </label>

                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control form-control-sm rounded-3 bg-light border-1 py-2 px-3 pe-5"
                      placeholder="e.g. XLM-XXXX-XXXX"
                      value={activationCode}
                      onChange={(e) => setActivationCode(e.target.value)}
                    />

                    <button
                      type="button"
                      className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-1 border-0"
                      onClick={async () => {
                        try {
                          const text = await navigator.clipboard.readText();
                          setActivationCode(text);
                        } catch (err) {
                          console.error("Failed to paste:", err);
                        }
                      }}
                      title="Paste"
                    >
                      <i className="bi bi-clipboard"></i>
                    </button>
                  </div>
                </div>

                <button
                  className="btn btn-dark w-100 rounded-3 py-3 fw-semibold d-flex align-items-center justify-content-center gap-2"
                  onClick={redeemActivationCode}
                  disabled={redeeming}
                  style={{ letterSpacing: "0.04em" }}
                >
                  {redeeming ? (
                    <>
                      <span className="spinner-border spinner-border-sm" />{" "}
                      Activating…
                    </>
                  ) : (
                    <>
                      <i className="bi bi-unlock-fill" /> Unlock Course
                    </>
                  )}
                </button>
                <p
                  className="text-center text-secondary mt-2 d-flex align-items-center justify-content-center gap-1"
                  style={{ fontSize: "0.68rem", letterSpacing: "0.05em" }}
                >
                  <i className="bi bi-shield-check" />
                  Each code is single-use and tied to your account
                </p>
              </>
            )}
            {/* ── PAYMENT TAB ── */}
            {activeTab === "payment" && (
              <>
                <h2 className="fs-4 fw-semibold text-dark mb-1">
                  Order summary
                </h2>
                <p className="text-secondary small mb-4">
                  Premium Lifetime Access
                </p>

                {/* Class select */}
                <div className="mb-3">
                  <label
                    className="form-label text-uppercase text-secondary fw-medium"
                    style={{ fontSize: "0.68rem", letterSpacing: "0.09em" }}
                  >
                    Select class
                  </label>
                  <select
                    className="form-select form-select-sm rounded-3 border-1 bg-light py-2 px-3"
                    value={selectedCourse}
                    onChange={(e) => setSelectedCourse(e.target.value)}
                  >
                    <option value="">Choose a class…</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Coupon */}
                <div className="mb-4">
                  <label
                    className="form-label text-uppercase text-secondary fw-medium d-flex align-items-center gap-1"
                    style={{ fontSize: "0.68rem", letterSpacing: "0.09em" }}
                  >
                    <i className="bi bi-tag" style={{ fontSize: "0.8rem" }} />
                    Coupon code
                  </label>
                  <div className="input-group">
                    <input
                      value={coupon}
                      onChange={(e) => setCoupon(e.target.value)}
                      className="form-control form-control-sm rounded-start-3 bg-light border-1"
                      disabled={applied}
                      placeholder={applied ? "Coupon applied" : "Enter code"}
                    />
                    <button
                      className={`btn btn-sm px-3 rounded-end-3 fw-medium ${applied ? "btn-outline-secondary" : "btn-dark"}`}
                      onClick={applied ? resetCoupon : handleApplyCoupon}
                      disabled={loadingCoupon}
                      style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}
                    >
                      {loadingCoupon ? (
                        <span className="spinner-border spinner-border-sm" />
                      ) : applied ? (
                        "Remove"
                      ) : (
                        "Apply"
                      )}
                    </button>
                  </div>
                  {applied && (
                    <div
                      className="d-flex align-items-center gap-1 mt-1 text-success"
                      style={{ fontSize: "0.78rem" }}
                    >
                      <i
                        className="bi bi-check-circle-fill"
                        style={{ fontSize: "0.8rem" }}
                      />
                      Coupon applied — saving ₹
                      {(priceBase - discountedSubtotal).toFixed(0)}
                    </div>
                  )}
                </div>

                {/* Price breakdown */}
                <div className="bg-light rounded-3 p-3 mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-secondary small">
                      Premium Lifetime Access
                    </span>
                    <span className="small">
                      ₹{priceBase.toLocaleString("en-IN")}
                    </span>
                  </div>

                  {applied && (
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-success small">Discount</span>
                      <span className="text-success small">
                        − ₹{(priceBase - discountedSubtotal).toFixed(0)}
                      </span>
                    </div>
                  )}

                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="text-secondary small">GST ({tax}%)</span>
                    <span className="small">
                      ₹{Math.round(taxAmount).toLocaleString("en-IN")}
                    </span>
                  </div>

                  <hr className="my-0 mb-3 border-secondary-subtle" />

                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-semibold">Total</span>
                    <span className="fw-bold fs-5">
                      ₹{finalAmount.toLocaleString("en-IN")}
                    </span>
                  </div>
                </div>

                {/* Pay button */}
                <button
                  className="btn btn-dark w-100 rounded-3 py-3 fw-semibold d-flex align-items-center justify-content-center gap-2"
                  onClick={handleCheckout}
                  disabled={paying}
                  style={{ letterSpacing: "0.04em" }}
                >
                  {paying ? (
                    <>
                      <span className="spinner-border spinner-border-sm" />{" "}
                      Processing…
                    </>
                  ) : (
                    <>
                      <i className="bi bi-lock-fill" /> Pay ₹
                      {finalAmount.toLocaleString("en-IN")}
                    </>
                  )}
                </button>

                <p
                  className="text-center text-secondary mt-2 d-flex align-items-center justify-content-center gap-1"
                  style={{ fontSize: "0.68rem", letterSpacing: "0.05em" }}
                >
                  <i className="bi bi-shield-check" />
                  Secured by Razorpay · 256-bit encryption
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
